/** @module csi/view/_CustomViewQueryMixin */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"underscore"
], function(declare, aspect, _) {

	/*
	 * This module is required to use when using both _CustomViewMixin and _QueryMixin with csi/view/Widget
	 */

	return declare(null, {
		/** @property {boolean} customViewSortExists - set to true if custom view contains sort settings */
		customViewSortExists: false,

		/**
		 * @override
		 * Return proper context for relative filter queries; this is necessary because in popup selection views,
		 * the context comes from the node starting the action rather than the node owning the popup view 
		 */
		getPreviewProperties() {
			const params = this.inherited(arguments);
			const ctx = this.getQueryRelativeContext();
			if (ctx) {
				params.relativeQueryContextUrl = ctx.getNodeURL();
			}

			return params;
		},

		/**
		 * @override
		 * Add logic before and after custom view has changed
		 */
		onViewCreate: function() {
			this.own(
				// use aspect.before so that this executes before any overrides to onViewPreferences in case
				// they rely on the correct query settings
				aspect.before(this, "onViewPreferences", () => {
					const {filterRemember, sortSettings, suspended, suspendedUntilScrolledIntoView} = this;

					// track whether or not custom view contains sort settings
					this.set("customViewSortExists", !!sortSettings?.length);

					// if query not part of custom view (not _CustomViewMixin), then reset when changing custom view
					if (!this.hasCustomViewOption("query")) {
						// only reset if there is already a store - if query is pre-defined on view, then this
						// should not reset them
						if (this.querySpecStore) {
							this.set("querySpecStore", null);
						}

						// load locally stored ad-hoc filter
						if (filterRemember) {
							this.loadAdHocFilters();
						}
					}

					if (suspendedUntilScrolledIntoView) {
						if (suspended) {
							// if custom view is already suspended, then it should stay suspended when scrolled into view
							this.suspendedUntilScrolledIntoView = false;
						} else {
							// act like view is suspended until user scrolls into view
							this.set("suspended", true);
						}
					}
				})
			);

			this.inherited(arguments);
		},

		/**
		 * Force creation of main level store to know which user defaults can be ad-hoc, and then update query with any;
		 * user defaults should not be re-applied if just refreshing/redrawing view
		 */
		setQueryDefaults: function() {
			if (this.loadingCustomView) {
				this._createMainStore();

				const qlMain = this.getMainQueryLevel();
				if (qlMain) {
					_.each(qlMain.unenforcedQueryObject, (value, key) => {
						this.setQueryValue(key, value);
					});
				}
			}
		},

		/**
		 * @override
		 * Remove any options that don’t apply to the view.
		 * @param {PreferenceView} cv
		 */
		onCreateDefaultCustomView(cv) {
			if (!this.canFilter()) {
				cv.removeCustomOption("filterSettings");
			}

			if (!this.canGroup()) {
				cv.removeCustomOption("groupSettings");
			}

			if (!this.canSort()) {
				cv.removeCustomOption("sortSettings");
			}

			if (!this.canReport()) {
				cv.removeCustomOption("reportSettings");
			}
		},

		/**
		 * Modify values from custom view due to migration or validation before applying to the view
		 * @param {number} version 
		 * @param {ICustomViewOptions} values 
		 */
		_migrateAndValidateCustomViewValues(version, values) {
			this.inherited(arguments);

			// Load Default Sort View when there is sort setting is empty
			if (values.sortSettings?.length === 0) {
				values.sortSettings = this.getDefaultCustomView().values.sortSettings;
			}
		},
	});
});